import React, { useState } from "react";
import { useSlateStatic, useSelected, useFocused, ReactEditor } from "slate-react";
import { Transforms } from "slate";
import { Popover, Space } from "antd";
import KoIcon from '@kwapio/icons';


const ImageElement = ({ attributes, element, children, url, readOnly }) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const selected = useSelected();
  const focused = useFocused();
  const [width, setWidth] = useState(element.width || 300);
  const [height, setHeight] = useState(element.height || 200);
  const [hovered, setHovered] = useState(false);

  const deleteImage = () => Transforms.removeNodes(editor, { at: path });
  const setAlignment = (align) => Transforms.setNodes(editor, { align }, { at: path });

  const handleResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
  
    setHovered(true); // Keep border visible
  
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = width;
    const startHeight = height;
  
    // Find the actual container (outer editable area or page width)
    const parentNode = e.currentTarget.closest(".slate-editor-wrapper") || document.body;
    const maxWidth = parentNode.getBoundingClientRect().width - 32; // subtract margin/padding buffer
  
    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
  
      let newWidth = startWidth;
      let newHeight = startHeight;
  
      if (direction.includes("right")) {
        newWidth = Math.min(Math.max(100, startWidth + deltaX), maxWidth);
      }
      if (direction.includes("bottom")) {
        newHeight = Math.max(100, startHeight + deltaY);
      }
  
      // Optional: Only allow bottom-right resize
      if (direction === "bottom-right") {
        newWidth = Math.min(Math.max(100, startWidth + deltaX), maxWidth);
        newHeight = Math.max(100, startHeight + deltaY);
      }
  
      setWidth(newWidth);
      setHeight(newHeight);
      Transforms.setNodes(editor, { width: newWidth, height: newHeight }, { at: path });
    };
  
    const onMouseUp = () => {
      setHovered(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  

  const renderCornerHandle = (position) => (
    
    <div
      key={`corner-${position}`}
      style={{
        position: "absolute",
        [position.includes("top") ? "top" : "bottom"]: "-6px",
        [position.includes("left") ? "left" : "right"]: "-6px",
        width: "2rem",
        height: "2rem",
        backgroundColor: "#1677ff",
        borderRadius: "50%",
        cursor: position === "top-right" ? `ne-resize` : position === "bottom-left" ? `sw-resize` : position === "bottom-right" ? `se-resize`  : `nw-resize`,
        zIndex: 10
      }}
      onMouseDown={(e) => handleResize(e, position)}
    />
  );

  const renderEdgeHandle = (edge) => {
    const styles = {
      position: "absolute",
      zIndex: 10,
      backgroundColor: "#1677ff",
      opacity: 0.5
    };

    if (edge === "top" || edge === "bottom") {
      styles[edge] = "-3px";
      styles.left = "0";
      styles.right = "0";
      styles.height = "6px";
      styles.cursor = `${edge}-resize`;
    } else {
      styles[edge] = "-3px";
      styles.top = "0";
      styles.bottom = "0";
      styles.width = "6px";
      styles.cursor = `${edge}-resize`;
    }

    return (
      <div
        key={`edge-${edge}`}
        style={styles}
        onMouseDown={(e) => handleResize(e, edge)}
      />
    );
  };

  const popoverContent = (
    <Space >
      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("left"); }}
      >
        <KoIcon name="LeftOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("center"); }}
      >
        <KoIcon name="CenterOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("right"); }}
      >
        <KoIcon name="RightOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer", color: "#ff4d4f" }}
        onMouseDown={(e) => { e.preventDefault(); deleteImage(); }}
      >
        <KoIcon name="DeleteOutlined" color="#FFF" width={14} height={14} />
      </div>

    </Space>
  );

  return (
    <div
      {...attributes}
      className="slate-editor-wrapper"
      onMouseEnter={() => !readOnly && setHovered(true)}
      onMouseLeave={() => !readOnly && setHovered(false)}
      contentEditable={false}
      onDragStart={(e) => {
        if (readOnly) return;
        const slateFragment = JSON.stringify({
          type: "image",
          url: url || element.url,
          width,
          height,
          align: element.align || "center",
          children: [{ text: "" }]
        });
        e.dataTransfer.setData("application/x-slate-fragment", slateFragment);
      }}
      style={{
        textAlign: element.align || "center",
        margin: "1rem 0",
        position: "relative"
      }}
    >
      {!readOnly ? (<Popover arrow={false} color="#222" content={popoverContent} placement="top" getPopupContainer={(triggerNode) => triggerNode.parentElement}>
        <div
          style={{
            maxWidth: "100%",
            width: `${width}px`,
            height: `${height}px`,
            overflow: "hidden",
            position: "relative",
            display: "inline-block",
            border: (hovered || (selected && focused)) ? "2px solid #1677ff" : "2px solid transparent",
            boxSizing: "border-box"
          }}
        >
          
          <img
            src={url || element.url}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              borderRadius: "8px",
              objectFit: "contain"
            }}
            onMouseDown={(e) => e.preventDefault()}
          />

          {hovered && (
            <>
              {["top", "right", "bottom", "left"].map(renderEdgeHandle)}
              {["top-left", "top-right", "bottom-left", "bottom-right"].map(renderCornerHandle)}
            </>
          )}
        </div>
      </Popover>) : <div
        style={{
          display: "inline-block",
          position: "relative",
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: "100%",
          borderRadius: "8px",
          boxSizing: "border-box",
          overflow: "hidden"
        }}
      >
          
        <img
          src={url || element.url}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            borderRadius: "8px",
            objectFit: "contain"
          }}
          onMouseDown={(e) => e.preventDefault()}
        />

        {hovered && (
          <>
            {["top", "right", "bottom", "left"].map(renderEdgeHandle)}
            {["top-left", "top-right", "bottom-left", "bottom-right"].map(renderCornerHandle)}
          </>
        )}
      </div>}
      {children}
    </div>
  );
};

export default ImageElement;
